import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { amazonUkSearchUrl } from "@/lib/affiliate";

export default function OwnerStockRequests() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_requests: 0, distinct_items: 0 });
  const [loading, setLoading] = useState(true);
  const [clearingKey, setClearingKey] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/stock-requests");
      setItems(data.items || []);
      setStats(data.stats || { total_requests: 0, distinct_items: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load stock requests");
    }
  };

  useEffect(() => { (async () => { try { await refresh(); } finally { setLoading(false); } })(); }, []);

  const clearItem = async (item) => {
    if (!confirm(`Clear all "${item.item_name}" requests from the board?`)) return;
    setClearingKey(item.item_key);
    try {
      await api.delete(`/admin/stock-requests/${encodeURIComponent(item.item_key)}`);
      toast.success(`Cleared ${item.count} request${item.count > 1 ? "s" : ""}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear");
    } finally {
      setClearingKey(null);
    }
  };

  if (loading) return <div className="text-[#5C6B61]">Loading stock requests…</div>;

  return (
    <section className="space-y-6" data-testid="owner-stock-requests">
      <header>
        <div className="ff-label">Customer demand</div>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Stock requests
        </h1>
        <p className="text-sm text-[#5C6B61] mt-2 max-w-2xl">
          When a customer can&apos;t find an ingredient at any supermarket and taps the &ldquo;Search Google&rdquo; fallback, we log it here. Use this to spot affiliate / partner opportunities, request new SKUs from your supermarket integrations, or curate a &ldquo;Hard-to-find&rdquo; pack.
        </p>
      </header>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Total requests" value={stats.total_requests} />
        <Stat label="Distinct items" value={stats.distinct_items} />
        <Stat label="Most-requested" value={items[0]?.item_name || "—"} accent="#3B5A3E" />
      </div>

      {items.length === 0 ? (
        <div className="ff-card p-8 text-center text-sm text-[#5C6B61]" data-testid="stock-requests-empty">
          No customer requests yet. When a customer taps &ldquo;Search Google&rdquo; from the Smart Swap modal, it&apos;ll appear here.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="stock-requests-list">
          {items.map((it) => (
            <li
              key={it.item_key}
              className="ff-card p-4 md:p-5 flex items-center gap-3 flex-wrap"
              data-testid={`stock-request-${it.item_key}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#FFF4E5] text-[#8A5C12] flex items-center justify-center text-xl font-semibold flex-none">
                {it.count}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#2A332C] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {it.item_name}
                </div>
                <div className="text-xs text-[#5C6B61] mt-1">
                  {it.user_count} customer{it.user_count !== 1 ? "s" : ""} · last asked {new Date(it.last_requested_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={`https://www.google.com/search?tbm=shop&hl=en-GB&gl=uk&q=${encodeURIComponent(it.item_name + " UK wholesale")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`stock-google-${it.item_key}`}
                  className="text-xs font-medium text-[#3B5A3E] hover:underline px-2 py-1"
                >
                  🔎 Source it
                </a>
                <a
                  href={amazonUkSearchUrl(it.item_name)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`stock-amazon-${it.item_key}`}
                  className="text-xs font-medium text-[#3B5A3E] hover:underline px-2 py-1"
                >
                  Amazon UK ↗
                </a>
                <button
                  onClick={() => clearItem(it)}
                  disabled={clearingKey === it.item_key}
                  data-testid={`stock-clear-${it.item_key}`}
                  className="text-xs text-[#A65541] hover:underline px-2 py-1 disabled:opacity-50"
                >
                  {clearingKey === it.item_key ? "Clearing…" : "Clear"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="ff-card p-4">
      <div className="ff-label">{label}</div>
      <div
        className="text-2xl font-semibold mt-1 truncate"
        style={{ color: accent || "#2A332C", fontFamily: "Outfit, sans-serif" }}
        title={String(value)}
      >
        {value}
      </div>
    </div>
  );
}
